import { Icon, IconName, IconSize } from './Icon';
import { Spinner } from './Spinner';
import { FontSize, FontWeight, Typography } from './Typography';

import React, { ButtonHTMLAttributes, FC } from 'react';
import { tv } from 'tailwind-variants';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export type ButtonVariant = 'primary' | 'secondary' | 'text' | 'text-error' | 'error' | 'accent';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  label?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  leadingIconName?: IconName;
  trailingIconName?: IconName;
  iconClassName?: string;
  disabled?: boolean;
  isLoading?: boolean;
  as?: React.ElementType;
}

export const buttonTV = tv({
  base: 'flex h-fit flex-row items-center justify-center break-keep outline-0 transition-all duration-300 ease-in-out focus:ring-1 focus:ring-other-outline focus:ring-offset-2',
  variants: {
    variant: {
      primary:
        'bg-sem-d-surf-primary text-sem-d-text-high hover:bg-sem-d-surf-primary-hover active:bg-sem-d-surf-primary-pressed',
      secondary:
        'border border-sem-l-divider-medium bg-sem-l-surf-primary text-sem-l-text-high hover:bg-sem-l-surf-primary-hover active:bg-sem-l-surf-primary-pressed',
      text: 'bg-sem-l-surf-primary text-sem-l-text-high hover:bg-sem-l-surf-primary-hover active:bg-sem-l-surf-primary-pressed',
      'text-error':
        'border border-sem-l-divider-medium bg-sem-l-surf-primary text-sem-l-text-error hover:bg-sem-l-surf-primary-hover active:bg-sem-l-surf-primary-pressed',
      error:
        'bg-sem-d-surf-error text-sem-d-text-high hover:bg-sem-d-surf-error-hover active:bg-sem-d-surf-error-pressed',
      accent: 'bg-sem-d-surf-accent text-sem-d-text-high hover:bg-sem-d-surf-accent-hover',
    },
    size: {
      xs: 'h-8 gap-x-0.5 rounded-md',
      sm: 'h-8 gap-x-1 rounded-md',
      md: 'h-[42px] gap-x-1.5 rounded-md',
      lg: 'h-14 gap-x-1.5 rounded-lg',
    },
    disabled: {
      true: 'border-hidden bg-sem-d-surf-primary-disabled text-sem-l-text-high-disabled hover:bg-sem-d-surf-primary-disabled',
    },
  },
});

const iconTV = tv({
  variants: {
    variant: {
      primary: 'fill-sem-d-icon-high',
      secondary: 'fill-sem-l-icon-high',
      text: 'fill-sem-l-icon-high',
      'text-error': 'fill-prim-cranberry-70',
      error: 'fill-sem-d-icon-high',
      accent: 'fill-sem-d-icon-high',
    },
    disabled: {
      true: 'fill-sem-l-icon-high-disabled',
    },
  },
});

const spinnerTV = tv({
  variants: {
    variant: {
      primary: 'border-sem-d-surf-primary border-b-sem-d-surf-primary-disabled',
      secondary: 'border-sem-d-surf-primary border-b-sem-d-surf-primary-disabled',
      error: 'border-sem-d-surf-primary border-b-sem-d-surf-primary-disabled',
      text: '',
      'text-error': '',
      accent: '',
    },
  },
});

const getFontSize = (size: ButtonSize): FontSize => {
  switch (size) {
    case 'xs':
      return 'rabbit';
    case 'sm':
      return 'rabbit';
    case 'md':
      return 'cat';
    case 'lg':
      return 'dog';
  }
};

const getFontWeight = (size: ButtonSize): FontWeight => {
  switch (size) {
    case 'xs':
      return 'regular';
    case 'sm':
      return 'bold';
    case 'md':
      return 'bold';
    case 'lg':
      return 'bold';
  }
};

const getIconSize = (size: ButtonSize): IconSize => {
  switch (size) {
    case 'xs':
      return '16';
    case 'sm':
      return '20';
    case 'md':
      return '20';
    case 'lg':
      return '20';
  }
};

const getPaddingClass = (size: ButtonSize, leadingIcon?: IconName, trailingIcon?: IconName) => {
  const basePadding = size === 'xs' ? 'px-2' : size === 'sm' ? 'px-3' : size === 'md' ? 'px-4' : 'px-4';
  const leadingPadding = leadingIcon
    ? size === 'xs'
      ? 'pl-1.5'
      : size === 'sm'
        ? 'pl-2'
        : size === 'md'
          ? 'pl-2.5'
          : 'pl-3.5'
    : '';
  const trailingPadding = trailingIcon
    ? size === 'xs'
      ? 'pr-1.5'
      : size === 'sm'
        ? 'pr-2'
        : size === 'md'
          ? 'pr-2.5'
          : 'pr-3.5'
    : '';
  return `${basePadding} ${leadingPadding} ${trailingPadding}`;
};

export const Button: FC<ButtonProps> = ({
  label,
  variant = 'primary',
  size = 'sm',
  leadingIconName,
  trailingIconName,
  iconClassName,
  className,
  disabled,
  isLoading = false,
  as: Container = 'button',
  ...props
}) => {
  const paddingClass = getPaddingClass(size, leadingIconName, trailingIconName);
  const buttonStyle = `${buttonTV({ variant, size, className, disabled })} ${paddingClass}`;
  const iconStyle = iconTV({ variant, disabled, className: iconClassName });
  const fontSize = getFontSize(size);
  const fontWeight = getFontWeight(size);
  const iconSize = getIconSize(size);
  const spinnerStyle = spinnerTV({ variant });
  return (
    <Container type="button" className={buttonStyle} disabled={disabled || isLoading} {...props}>
      {isLoading && <Spinner size="sm" className={spinnerStyle} />}
      {leadingIconName && !isLoading && <Icon name={leadingIconName} size={iconSize} className={iconStyle} />}
      {label && (
        <Typography size={fontSize} weight={fontWeight} className="text-inherit">
          {label}
        </Typography>
      )}
      {trailingIconName && <Icon name={trailingIconName} size={iconSize} className={iconStyle} />}
    </Container>
  );
};
